/**
 * What the viewer frame shows, decided from CMS data alone (M2, Q4).
 *
 * Nothing here reads the iframe. A cross-origin frame cannot be interrogated —
 * `onload` fires for a 404 page, for a redirect to English, and for a real
 * article alike — so every availability question is answered from the API
 * payload *before* a frame is ever mounted. That is why these are pure
 * functions over the cluster detail rather than callbacks on an element.
 */

import type { BlogClusterDetail, BlogLocaleEntry } from "./types";

/**
 * The marketing origin admin frames when nothing is configured.
 *
 * Derived from the environment, and it has to be, because this is not a rarely
 * taken path: `VITE_MARKETING_URL` is set nowhere today — `.env.example` ships
 * it blank, and neither `.github/workflows/ci.yml` nor `deploy.yml` passes it —
 * so the fallback IS the value every deployed admin runs on. While it was the
 * single literal `https://dev.atsready.ai`, staging and production admin both
 * framed DEV blog content in the viewer and in the media panel, and nothing in
 * the UI said so.
 *
 * `VITE_ENVIRONMENT` is the signal because it is the one admin already has:
 * `deploy.yml` passes the deploy environment (`dev` | `production`) and
 * `ci.yml` passes `development` | `production`. Both spellings of the dev case
 * land on the same answer.
 *
 * Anything unrecognised resolves to dev rather than to production: a wrong
 * environment string must not point an internal tool at live content. And the
 * unknown case is deliberately NOT localhost — admin's CSP is `frame-src
 * https://*.atsready.ai` and nothing else, so a local marketing server could
 * not be framed even when one is running.
 */
export function defaultMarketingBaseUrl(environment: string | undefined): string {
  const env = (environment ?? "").trim();
  if (env === "production") return "https://atsready.ai";
  return "https://dev.atsready.ai";
}

/**
 * Normalise a configured marketing origin.
 *
 * Trailing slashes are stripped so URL building never produces `//`; an unset
 * or blank value falls back to {@link defaultMarketingBaseUrl} rather than
 * producing `undefined/en/blog/...`.
 *
 * A blank value is NOT a hard failure, which was the alternative considered.
 * `MARKETING_BASE_URL` in `src/config/env.ts` is a module-scope constant, and
 * that module is imported by `services/api-client.ts` and `contexts/
 * AuthContext.tsx` — admin's boot path, not just the blog page. Throwing here
 * would take the whole bundle down at module evaluation, login included, in
 * every environment including local dev, because the variable is unset in all
 * of them. The silent-wrong-host problem is fixed by making the default
 * correct per environment, not by removing it.
 */
export function resolveMarketingBaseUrl(
  raw: string | undefined,
  environment: string | undefined,
): string {
  const trimmed = (raw ?? "").trim();
  if (trimmed === "") return defaultMarketingBaseUrl(environment);
  return trimmed.replace(/\/+$/, "");
}

/**
 * The preview deployment's origin for a given build.
 *
 * The preview Pages project is deliberately on an `atsready.ai` hostname (spec
 * A3) so admin's `frame-src https://*.atsready.ai` covers it without widening
 * the policy for one throwaway deployment.
 */
export function previewOriginFor(baseUrl: string, previewBuildId: string): string {
  return baseUrl.replace(/^(https?:\/\/)/, `$1preview-${previewBuildId}.`);
}

/** The public address of one locale copy. */
export function blogPostUrl(args: {
  origin: string;
  locale: string;
  slug: string;
  pathSlug: string | null | undefined;
}): string {
  const segment = args.pathSlug ?? args.slug;
  return `${args.origin}/${args.locale}/blog/${segment}`;
}

/**
 * Reserved first path segment of marketing's chrome-less preview route.
 *
 * A string contract across two packages: marketing declares the same segment
 * as `PREVIEW_SEGMENT` in `src/lib/i18n/locales.ts` and prerenders
 * `/preview/{locale}/blog/{slug}` in its own root route group. There is no
 * shared module to hold it — admin does not depend on marketing — so it is
 * written down in both places and named in both sets of tests.
 */
export const BLOG_PREVIEW_SEGMENT = "preview";

/**
 * The address the frame actually loads: the post, and nothing else.
 *
 * The public page renders the whole site around the article — header, footer,
 * CTA band — and an author reviewing a draft could click into `/pricing` and
 * wander off with no way back. This route is the same article rendered by the
 * same code with none of that: no chrome, no nav, and no link that leaves.
 *
 * It is a real prerendered page rather than a mode of the public one, because
 * marketing is a static export: a query parameter cannot change prerendered
 * HTML, and stripping the chrome in the browser would flash the full site
 * first. The iframe's `sandbox` cannot substitute for it either — no sandbox
 * token stops a frame navigating ITSELF.
 *
 * Same locale and same address slug as {@link blogPostUrl}, so the preview is
 * the copy under review and not a negotiated one.
 */
export function blogPreviewUrl(args: {
  origin: string;
  locale: string;
  slug: string;
  pathSlug: string | null | undefined;
}): string {
  const segment = args.pathSlug ?? args.slug;
  return `${args.origin}/${BLOG_PREVIEW_SEGMENT}/${args.locale}/blog/${segment}`;
}

export type FrameMode = "published" | "draft";

export function isFrameMode(value: string | null): value is FrameMode {
  return value === "published" || value === "draft";
}

export type FrameDecision =
  | { kind: "render"; url: string; tag: string; caption: string }
  | { kind: "locale-missing"; url: string; locale: string }
  | { kind: "never-published"; url: string }
  | { kind: "preview-missing" }
  | { kind: "preview-stale"; builtAt: string };

export interface FrameDecisionArgs {
  cluster: Pick<BlogClusterDetail, "slug" | "previewBuildId" | "previewBuiltAt" | "previewStale">;
  entry: BlogLocaleEntry;
  mode: FrameMode;
  baseUrl: string;
}

/**
 * Decide what the frame region renders.
 *
 * Order matters: a locale that was never written has no address in *either*
 * frame, so that check precedes the published/draft split. Answering "the
 * preview is stale" for a locale that does not exist would send the reviewer
 * to rebuild a preview that still would not contain the copy.
 */
export function decideFrame(args: FrameDecisionArgs): FrameDecision {
  const { cluster, entry, mode, baseUrl } = args;
  // The PUBLIC address, which is what the blocked states are about: "this URL
  // does not exist" and "nothing is live here" are statements about the page
  // readers would visit, not about the frame's internal target.
  const liveUrl = blogPostUrl({
    origin: baseUrl,
    locale: entry.locale,
    slug: cluster.slug,
    pathSlug: entry.pathSlug,
  });

  if (!entry.exists) {
    return { kind: "locale-missing", url: liveUrl, locale: entry.locale };
  }

  if (mode === "published") {
    if (entry.publishedVersion === null || entry.publishedVersion === undefined) {
      return { kind: "never-published", url: liveUrl };
    }
    return {
      kind: "render",
      url: blogPreviewUrl({
        origin: baseUrl,
        locale: entry.locale,
        slug: cluster.slug,
        pathSlug: entry.pathSlug,
      }),
      tag: "Live",
      caption:
        "Live render from the marketing site — the real renderer, not a preview of the editor. " +
        "The site chrome is omitted so the frame cannot lead anywhere but this post.",
    };
  }

  if (cluster.previewBuildId === null || cluster.previewBuiltAt === null) {
    return { kind: "preview-missing" };
  }
  if (cluster.previewStale) {
    return { kind: "preview-stale", builtAt: cluster.previewBuiltAt };
  }

  return {
    kind: "render",
    url: blogPreviewUrl({
      origin: previewOriginFor(baseUrl, cluster.previewBuildId),
      locale: entry.locale,
      slug: cluster.slug,
      pathSlug: entry.pathSlug,
    }),
    tag: "Preview",
    caption:
      "Preview render — same renderer as production, different deployment. " +
      "The site chrome is omitted so the frame cannot lead anywhere but this post.",
  };
}

/**
 * The always-visible sentence naming which frame is on screen (Q3).
 *
 * The toggle defaults to published, and the label is never implicit: a reviewer
 * who publishes what they *think* they are looking at is the failure this
 * sentence exists to prevent.
 */
export function frameModeLabel(mode: FrameMode): string {
  return mode === "published"
    ? "Showing the published build — what readers see right now"
    : "Showing the preview build — not public, not indexed";
}

/**
 * The per-locale version pair for the header (Q1).
 *
 * Versions are per locale copy. A locale that has never published says so in
 * words rather than rendering `v0` or an em dash.
 */
export function describeLocaleVersions(entry: BlogLocaleEntry): {
  served: string;
  current: string;
  upToDate: boolean;
} {
  const current = entry.version === undefined ? "not written" : `v${String(entry.version)}`;
  if (entry.publishedVersion === null || entry.publishedVersion === undefined) {
    return { served: "no version is live", current, upToDate: false };
  }
  return {
    served: `readers see v${String(entry.publishedVersion)}`,
    current: `current ${current}`,
    upToDate: entry.publishedVersion === entry.version,
  };
}
